import { danhSachHanhDong } from './danh-muc.ts';
import { nhanDonVi } from './don-vi.ts';
import type { SuKien, TuyChonLoc as TuyChonLocApi } from './types.ts';

export interface ChonDuoc {
  value: string;
  label: string;
  /** Khoá React. Khác `value` khi hai dòng cùng trỏ về một giá trị lọc. */
  key?: string;
  /** Chú thích khi rê chuột: mã gốc của dòng không có tên. */
  title?: string;
}

export interface TuyChonManHinh {
  nguoi: ChonDuoc[];
  donVi: ChonDuoc[];
  thaoTac: ChonDuoc[];
  /** Danh mục bị cắt ở 500 giá trị mỗi nhóm — ô lọc phải nói ra. */
  biCat: boolean;
}

/** Nhãn của một mã hành động theo ngôn ngữ đang xem — `useNhanHanhDong()` dựng hàm này. Mã chưa khai thì trả mã. */
export type NhanMa = (ma: string) => string;

export type SoSanhChon = (a: ChonDuoc, b: ChonDuoc) => number;

/** Sắp theo nhãn, theo thứ tự chữ của ngôn ngữ đang xem (`useT().locale`). */
export function sanhTheo(locale: string): SoSanhChon {
  return (a, b) => a.label.localeCompare(b.label, locale);
}

/** Tên đơn vị chụp trong các bản ghi đang hiển thị — nguồn phụ khi danh mục không mang tên. */
function tenDonVi(dong: readonly SuKien[]): Map<string, string> {
  const bang = new Map<string, string>();
  for (const suKien of dong) {
    const donVi = suKien.nguoi_thuc_hien?.don_vi;
    if (donVi?.id && donVi.ten) bang.set(donVi.id, donVi.ten);
  }
  return bang;
}

function idDonVi(dong: readonly SuKien[]): Set<string> {
  const ids = new Set<string>();
  for (const suKien of dong) {
    const id = suKien.nguoi_thuc_hien?.don_vi?.id;
    if (id) ids.add(id);
  }
  return ids;
}

/**
 * Tuỳ chọn cho ba ô lọc, từ danh mục `/bo-loc` của audit-service.
 *
 * ── MỘT NGƯỜI ĐỔI TÊN THÀNH HAI DÒNG, VÀ ĐÓ LÀ ĐÚNG ──────────────────────
 * `actor_ten` là bản chụp lúc ghi, nên cùng một `actor_id` có thể về kèm hai tên. KHÔNG gộp: tên cũ là thứ người đi
 * tra cứu còn nhớ. Hai dòng cùng `value`, khác `key`.
 *
 * ── `facet` CHƯA VỀ THÌ TẠM DÙNG DỮ LIỆU ĐÃ TẢI ───────────────────────────
 * Danh mục đang tải hoặc lỗi thì ô lọc gom từ các bản ghi đang hiển thị chứ không rỗng: một ô lọc rỗng làm người dùng
 * tưởng không có dữ liệu để lọc, trong khi danh sách bên cạnh đang đầy.
 */
export function tuyChonLoc(
  facet: TuyChonLocApi | undefined,
  dong: readonly SuKien[],
  nhanMa: NhanMa,
  danhBa: ReadonlyMap<string, string> = new Map(),
  locale = 'vi',
): TuyChonManHinh {
  const sanh = sanhTheo(locale);
  const ten = tenDonVi(dong);

  if (facet) {
    return {
      nguoi: facet.nguoi
        .map((n, i) => ({ value: n.actor_id, label: n.actor_ten ?? n.actor_id, key: `${n.actor_id}#${i}` }))
        .sort(sanh),
      donVi: facet.don_vi
        .map((d, i) => ({
          value: d.actor_don_vi_id,
          label: nhanDonVi(d.actor_don_vi_id, danhBa, d.actor_don_vi_ten ?? ten.get(d.actor_don_vi_id)),
          key: `${d.actor_don_vi_id}#${i}`,
        }))
        .sort(sanh),
      thaoTac: facet.hanh_dong.map((ma) => ({ value: ma, label: nhanMa(ma) })).sort(sanh),
      biCat: facet.bi_cat === true,
    };
  }

  const nguoi = new Map<string, string>();
  for (const suKien of dong) {
    const nth = suKien.nguoi_thuc_hien;
    if (nth?.id) nguoi.set(nth.id, nth.ten ?? nth.id);
  }
  return {
    nguoi: [...nguoi.entries()].map(([value, label]) => ({ value, label })).sort(sanh),
    donVi: [...idDonVi(dong)]
      .map((value) => ({ value, label: nhanDonVi(value, danhBa, ten.get(value)) }))
      .sort(sanh),
    // Cả danh mục, vì không biết khoảng thời gian này có những mã nào.
    thaoTac: danhSachHanhDong()
      .map((ma) => ({ value: ma, label: nhanMa(ma) }))
      .sort(sanh),
    // Danh mục chưa về thì chưa có gì bị cắt — nói "đã cắt" lúc này là nói sai.
    biCat: false,
  };
}
